import { Box, TextField as MUITextField, Popper, styled } from "@mui/material";
import { generateFormFieldStyles } from "components/helpers";
import { base, gray, primary } from "configs/colors";
import { shadows } from "configs/shadows";
import { rem } from "utils";

export const Container = styled(Box)(({ theme }) => ({
  display: "block",

  // Input Label
  "& > .MuiFormLabel-root": {
    marginBottom: rem(6),
  },

  // Helper text
  ".MuiFormHelperText-root": {
    marginTop: rem(6),
    "&, &.Mui-disabled": {
      color: gray[600],
    },
    "&.Mui-error": {
      color: theme.palette.error.main,
    },
  },

  ".MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
  },

  ".MultiAutocomplete-Tag": {
    margin: rem(2),
  },
}));

export const StyledPopper = styled(Popper)({
  ".MuiAutocomplete-paper": {
    zIndex: 1400,
    boxShadow: shadows.lg,
    ".MuiAutocomplete-listbox": {
      padding: `${rem(4)} ${rem(6)}`,
      ".MuiAutocomplete-option": {
        justifyContent: "space-between",
        alignItems: "center",
        padding: `${rem(8)} ${rem(6)}`,
        borderRadius: "6px",
        "&:hover": {
          backgroundColor: gray[50],
        },
        '&[aria-selected="true"]': {
          backgroundColor: `${gray[50]} !important`,
          "&:hover": { backgroundColor: `${gray[50]} !important` },
        },
      },
      ".MuiAutocomplete-option + .MuiAutocomplete-option": {
        marginTop: rem(4),
      },
      // Group
      ".MuiAutocomplete-groupLabel": {
        display: "flex",
        alignItems: "center",
        gap: rem(8),
        margin: `${rem(4)} 0`,
        padding: `${rem(6)} ${rem(10)}`,
        fontSize: rem(12),
        lineHeight: rem(18),
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: gray[700],
        backgroundColor: gray[100],
        borderRadius: "6px",
        "&::before": {
          content: '""',
          width: rem(3),
          height: rem(14),
          borderRadius: rem(2),
          backgroundColor: primary[600],
        },
      },
      ".MuiAutocomplete-groupUl": {
        padding: `0 0 0 ${rem(8)}`,
      },
      "li:not(.MuiAutocomplete-option) + li:not(.MuiAutocomplete-option)": {
        marginTop: rem(4),
        paddingTop: rem(4),
        borderTop: `1px solid ${gray[200]}`,
      },
    },
  },
});

export const SelectedIconWrapper = styled(Box)(() => ({
  display: "inline-flex",
  justifyContent: "center",
  alignItems: "center",
  marginLeft: rem(8),
  marginRight: rem(2),
}));

export const Input = styled(MUITextField)(({ theme, color }) => {
  const styles = generateFormFieldStyles({ theme, color });

  return {
    marginTop: 0,
    boxShadow: styles.normal.boxShadow,
    borderRadius: theme.shape.borderRadius,

    // Input root
    ".MuiInputBase-root": {
      backgroundColor: styles.normal.backgroundColor,
    },

    // Autocomplete Clear Icon
    ".MuiAutocomplete-clearIndicator": {
      color: gray[500],
    },

    // Autocomplete Popup Icon
    ".MuiAutocomplete-popupIndicator": {
      color: gray[500],
      padding: rem(4),
    },

    // Input element
    ".MuiInputBase-input": {
      padding: `${rem(2)} ${rem(2)} ${rem(2)} ${rem(6)} !important`,
      height: rem(20),
    },

    // Fieldset
    ".MuiOutlinedInput-notchedOutline": {
      borderWidth: "1px !important",
      borderColor: styles.normal.borderColor,
      transition: theme.transitions.create([
        "border-color",
        "background-color",
        "box-shadow",
      ]),
    },

    // Helper text
    ".MuiFormHelperText-root": {
      marginLeft: 0,
      marginRight: 0,
      marginTop: rem(6),
      "&, &.Mui-disabled": {
        color: gray[600],
      },
    },

    // Disable
    ".Mui-disabled": {
      "&.MuiInputBase-root": {
        backgroundColor: styles.disabled.backgroundColor,
      },

      "&.MuiInputBase-input": {
        "-webkit-text-fill-color": styles.disabled.color,
      },

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: `${styles.disabled.borderColor} !important`,
      },
    },

    // Without error
    "&:not(.Mui-error)": {
      // Hover
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.normal.hover.borderColor,
      },
      // Focus
      ".Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.normal.focus.borderColor,
        boxShadow: styles.normal.focus.boxShadow,
      },
    },

    // Error
    ".Mui-error": {
      // Hover
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.error.hover.borderColor,
      },
      // Focus
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: styles.error.focus.borderColor,
        boxShadow: styles.error.focus.boxShadow,
      },
    },
  };
});

export const Tag = styled(Box)<{ disabled?: boolean }>(({ disabled }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "1px 2px 1px 3px",
  borderRadius: "6px",
  border: `1px solid ${gray[300]}`,
  backgroundColor: base.White,
  boxSizing: "border-box",
  color: gray[700],
  opacity: disabled ? 0.7 : 1,
}));
